import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { defaultTransition } from '../../theme'

import { theseus, theseus2 } from '../../api/theseus'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'

import { Box } from '../UI/Box'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import { setLithiumAccount } from '../../modules/user/slice'
import ThemedButton from '../ThemedButton'
import EmbeddableModal from '../EmbeddableModal'
import AnimatedLoader from '../AnimatedLoader'
import useWeb3 from '../../hooks/useWeb3'

const Link = styled.span`
  span.active {
    color: ${({ theme }) => theme.colors.brandPrimary};
    ${defaultTransition}
    :hover {
      cursor: pointer;
      color: ${({ theme }) => theme.colors.brandSecondary};
    }
  }
`

const UserName = styled.span`
  text-wrap: nowrap;
`

const ConnectedAccount = styled(Box)`
  flex-grow: 0;
  button {
    ${defaultTransition}
  }
`

const ConnectButton = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const dataOnAuth = useCallback(
    async (user: any) => {
      try {
        const { data } = await theseus2.post('/user/update/telegram', {
          telegramHandle:
            user.username ??
            `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ??
            user.id,
          telegramUserId: user.id,
        })
        dispatch(setLithiumAccount(data?.updatedUser))
      } catch (error) {
        console.error(error)
        const message = error.data?.message ?? 'Failed to connect telegram'
        toast.error(message)
      }
    },
    [dispatch],
  )

  useEffect(() => {
    const dataAuthUrl = ''

    if (!!dataAuthUrl === !!dataOnAuth) {
      throw new Error(
        'One of this props should be defined: dataAuthUrl (Redirect URL), dataOnAuth (callback fn) should be defined.',
      )
    }

    if (dataOnAuth) {
      ;(window as any).telegramLoginWidgetCb = dataOnAuth
    }

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.async = true

    const attributes = {
      'data-telegram-login': process.env.REACT_APP_TELEGRAM_BOT_NAME,
      'data-size': 'large',
      'data-request-access': 'write',
      'data-userpic': true,
      'data-lang': 'en',
      'data-auth-url': dataAuthUrl,
      'data-onauth': 'telegramLoginWidgetCb(user)',
    }

    for (const [k, v] of Object.entries(attributes)) {
      v !== undefined && script.setAttribute(k, `${v}`)
    }

    containerRef.current?.appendChild(script)

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }
      if ((window as any).telegramLoginWidgetCb) {
        delete (window as any).telegramLoginWidgetCb
      }
    }
  }, [dataOnAuth])

  return (
    <Box c dc>
      <div ref={containerRef} />
    </Box>
  )
}

const TelegramConnectModal = ({
  isVisible,
  closeModal,
}: {
  isVisible: boolean
  closeModal: () => void
}) => {
  return (
    <EmbeddableModal
      isVisible={isVisible}
      dismiss={closeModal}
      hideBolt
      minHeight={250}
      minWidth={300}
    >
      <Box dc c alignSelf="center" g={16}>
        Please connect your telegram account
        <ConnectButton />
      </Box>
    </EmbeddableModal>
  )
}

function TelegramConnectButton({ label = 'Connect' }: { label?: string }) {
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
  const [isLoadingDisconnect, setIsLoadingDisconnect] = useState(false)
  const { user, isLoggedIn } = useIsLoggedIn()
  const { initWeb3 } = useWeb3()
  const dispatch = useDispatch()

  const { telegramHandle } = user ?? { telegramHandle: '' }

  const disconnect = useCallback(
    async (user: any) => {
      try {
        setIsLoadingDisconnect(true)
        const {
          data: { updatedUser },
        } = await theseus.post('/user/update/telegram', {
          telegramHandle: '',
          telegramUserId: '',
          isDisconnect: true,
        })
        dispatch(setLithiumAccount(updatedUser))
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoadingDisconnect(false)
        setIsConnectModalOpen(false)
      }
    },
    [dispatch],
  )

  if (!isLoggedIn) {
    return (
      <Link>
        <ThemedButton
          $styleType="surface-chip"
          text="Connect"
          onClick={() => initWeb3()}
        />
      </Link>
    )
  }

  if (!!telegramHandle) {
    return (
      <ConnectedAccount ac g={16} className="force-row">
        <UserName>{telegramHandle}</UserName>
        <ThemedButton
          $styleType="surface-chip"
          onClick={disconnect}
          text={
            isLoadingDisconnect ? <AnimatedLoader size={16} /> : 'Disconnect'
          }
        />
      </ConnectedAccount>
    )
  }

  return (
    <Link>
      <ThemedButton
        $styleType="surface-chip"
        onClick={() => setIsConnectModalOpen(true)}
        text={label}
      />
      <TelegramConnectModal
        isVisible={isConnectModalOpen}
        closeModal={() => setIsConnectModalOpen(false)}
      />
    </Link>
  )
}

export default TelegramConnectButton
